import { useMemo, useState, Fragment } from "react";
import {
  useAdminListAuditLog,
  getAdminListAuditLogQueryKey,
  type AuditLogEvent,
  type AdminListAuditLogParams,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, Search, ChevronDown, ChevronRight } from "lucide-react";
import { useDebounce } from "@/hooks/use-debounce";

const PAGE_SIZE = 50;

// Convert the local datetime-input value into an ISO string the API
// accepts. Returns undefined for empty/invalid input so the param is
// dropped from the query rather than sent as `?since=`.
function localInputToIso(value: string): string | undefined {
  if (!value) return undefined;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

// Convert an ISO timestamp (as embedded in audit-alert deep links) into
// the `YYYY-MM-DDTHH:mm` format the <input type="datetime-local"> needs.
// The browser's datetime-local input interprets that string as local
// time, so we project the ISO instant into the user's local zone via
// getFullYear/getMonth/etc. Returns "" for empty/invalid input so the
// filter stays untouched.
function isoToLocalInput(value: string | null): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// One-shot read of the URL search string at module load time. Lets the
// audit-alert email links pre-populate the filter inputs (actorUserId,
// since, until) without forcing the page to subscribe to URL changes —
// once the user starts typing, their state takes over.
function readInitialFiltersFromUrl(): {
  actorUserId: string;
  since: string;
  until: string;
} {
  if (typeof window === "undefined") {
    return { actorUserId: "", since: "", until: "" };
  }
  const params = new URLSearchParams(window.location.search);
  const actorRaw = params.get("actorUserId") ?? "";
  // Tolerate trailing/leading whitespace in the URL but reject obvious
  // junk so a malformed link doesn't pin a non-numeric filter into the
  // input.
  const actorClean = actorRaw.trim();
  const actorUserId = /^\d+$/.test(actorClean) ? actorClean : "";
  return {
    actorUserId,
    since: isoToLocalInput(params.get("since")),
    until: isoToLocalInput(params.get("until")),
  };
}

function fmtTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

// Pretty-print a JSON snapshot for the expanded diff row. Falls back
// to JSON.stringify if it isn't an object — the recorder always
// stores a plain object, but we tolerate the API's `unknown` type.
function fmtJson(value: unknown): string {
  if (value === null || value === undefined) return "—";
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export default function AuditLogPage() {
  // Lazy initializers so the URL is read once at mount. The inputs
  // become controlled by user state immediately after — we never
  // re-sync from the URL, so reloading clears any audit-alert deep-link
  // pre-fill that the user has since edited.
  const initialFilters = useMemo(readInitialFiltersFromUrl, []);
  const [page, setPage] = useState(0);
  const [actorUserIdInput, setActorUserIdInput] = useState(
    initialFilters.actorUserId,
  );
  const [eventType, setEventType] = useState("");
  const [targetType, setTargetType] = useState("");
  const [search, setSearch] = useState("");
  const [since, setSince] = useState(initialFilters.since);
  const [until, setUntil] = useState(initialFilters.until);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  // Debounce the free-text search so we aren't refetching on every
  // keystroke. The other filters fire via dropdown / date pickers and
  // are user-driven discrete events, so they don't need debouncing.
  const debouncedSearch = useDebounce(search, 250);

  const params = useMemo<AdminListAuditLogParams>(() => {
    const out: AdminListAuditLogParams = {
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    };
    const actorIdNum = Number.parseInt(actorUserIdInput.trim(), 10);
    if (Number.isFinite(actorIdNum) && actorIdNum > 0) {
      out.actorUserId = actorIdNum;
    }
    const trimmedEvent = eventType.trim();
    if (trimmedEvent) out.eventType = trimmedEvent;
    const trimmedTarget = targetType.trim();
    if (trimmedTarget) out.targetType = trimmedTarget;
    const trimmedQ = debouncedSearch.trim();
    if (trimmedQ) out.q = trimmedQ;
    const sinceIso = localInputToIso(since);
    if (sinceIso) out.since = sinceIso;
    const untilIso = localInputToIso(until);
    if (untilIso) out.until = untilIso;
    return out;
  }, [page, actorUserIdInput, eventType, targetType, debouncedSearch, since, until]);

  const { data, isLoading, isFetching, error } = useAdminListAuditLog(params, {
    query: {
      queryKey: getAdminListAuditLogQueryKey(params),
      refetchOnWindowFocus: false,
    },
  });

  const events = data?.events ?? [];
  const total = data?.totalCount ?? 0;
  const startIdx = page * PAGE_SIZE;
  const endIdx = Math.min(startIdx + events.length, total);

  const toggleExpanded = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const resetFilters = () => {
    setActorUserIdInput("");
    setEventType("");
    setTargetType("");
    setSearch("");
    setSince("");
    setUntil("");
    setPage(0);
  };

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Audit Log</h1>
        <p className="text-sm text-muted-foreground">
          Workspace-wide record of mutating actions. Newest events first.
        </p>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <Label htmlFor="audit-search">Search</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="audit-search"
                  className="pl-8"
                  placeholder="Description, actor, event…"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(0);
                  }}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="audit-event-type">Event type</Label>
              <Input
                id="audit-event-type"
                placeholder="e.g. template.updated"
                value={eventType}
                onChange={(e) => {
                  setEventType(e.target.value);
                  setPage(0);
                }}
              />
            </div>
            <div>
              <Label htmlFor="audit-target-type">Target type</Label>
              <Input
                id="audit-target-type"
                placeholder="e.g. template"
                value={targetType}
                onChange={(e) => {
                  setTargetType(e.target.value);
                  setPage(0);
                }}
              />
            </div>
            <div>
              <Label htmlFor="audit-actor">Actor user id</Label>
              <Input
                id="audit-actor"
                inputMode="numeric"
                placeholder="local users.id"
                value={actorUserIdInput}
                onChange={(e) => {
                  setActorUserIdInput(e.target.value);
                  setPage(0);
                }}
              />
            </div>
            <div>
              <Label htmlFor="audit-since">Since</Label>
              <Input
                id="audit-since"
                type="datetime-local"
                value={since}
                onChange={(e) => {
                  setSince(e.target.value);
                  setPage(0);
                }}
              />
            </div>
            <div>
              <Label htmlFor="audit-until">Until</Label>
              <Input
                id="audit-until"
                type="datetime-local"
                value={until}
                onChange={(e) => {
                  setUntil(e.target.value);
                  setPage(0);
                }}
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button variant="outline" size="sm" onClick={resetFilters}>
              Reset filters
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">
            Events
            <span className="ml-2 text-sm text-muted-foreground font-normal">
              {total > 0
                ? `Showing ${startIdx + 1}–${endIdx} of ${total}`
                : "No events"}
            </span>
          </CardTitle>
          {isFetching && !isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : null}
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="text-sm text-destructive">
              Failed to load audit log. Please try again.
            </div>
          ) : events.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              No events match the current filters.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead className="w-[180px]">When</TableHead>
                  <TableHead className="w-[200px]">Actor</TableHead>
                  <TableHead className="w-[180px]">Event</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-[160px]">Target</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.map((ev: AuditLogEvent) => {
                  const isOpen = expanded.has(ev.id);
                  const hasDiff = ev.before !== null || ev.after !== null;
                  return (
                    <Fragment key={ev.id}>
                      <TableRow
                        className={hasDiff ? "cursor-pointer" : undefined}
                        onClick={hasDiff ? () => toggleExpanded(ev.id) : undefined}
                      >
                        <TableCell>
                          {hasDiff ? (
                            isOpen ? (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            )
                          ) : null}
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {fmtTimestamp(ev.createdAt)}
                        </TableCell>
                        <TableCell className="text-sm">
                          {ev.actorEmail ?? (
                            <span className="text-muted-foreground italic">
                              {ev.actorUserId !== null
                                ? `user #${ev.actorUserId}`
                                : "system"}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="font-mono text-xs">
                            {ev.eventType}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{ev.description}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {ev.targetType}
                          {ev.targetId ? `:${ev.targetId}` : ""}
                        </TableCell>
                      </TableRow>
                      {isOpen && hasDiff ? (
                        <TableRow>
                          <TableCell />
                          <TableCell colSpan={5}>
                            <div className="grid gap-4 md:grid-cols-2 py-2">
                              <div>
                                <div className="text-xs font-semibold text-muted-foreground mb-1">
                                  Before
                                </div>
                                <pre className="text-xs bg-muted rounded p-2 overflow-x-auto whitespace-pre-wrap">
                                  {fmtJson(ev.before)}
                                </pre>
                              </div>
                              <div>
                                <div className="text-xs font-semibold text-muted-foreground mb-1">
                                  After
                                </div>
                                <pre className="text-xs bg-muted rounded p-2 overflow-x-auto whitespace-pre-wrap">
                                  {fmtJson(ev.after)}
                                </pre>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}

          <div className="mt-4 flex items-center justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={endIdx >= total}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
